import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer, ArrowLeft } from "@phosphor-icons/react";

// Same date helpers as Schedule.jsx – kept local to avoid a shared-utils file.
const iso = (d) => d.toISOString().slice(0, 10);
const parseIso = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const weekStart = (d) => {
  const x = new Date(d); const day = x.getDay();
  const diff = (day + 6) % 7;
  x.setDate(x.getDate() - diff); x.setHours(0, 0, 0, 0);
  return x;
};
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const prettyDate = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

// Bilingual labels for the header/footer
const I18N = {
  en: { kicker: "SHOP FLOOR", title: "WEEKLY SCHEDULE", weekOf: "Week of", stat: "STAFF · SHIFTS", printed: "Printed", noshifts: "No shifts scheduled for this week.", staff: "Staff", today: "Today", legendShift: "Shift window", legendEmpty: "· = no shift assigned", signoff: "Supervisor sign-off:", floor: "Post on shop floor. Update as changes occur." },
  es: { kicker: "TALLER", title: "HORARIO SEMANAL", weekOf: "Semana del", stat: "PERSONAL · TURNOS", printed: "Impreso", noshifts: "Sin turnos programados esta semana.", staff: "Personal", today: "Hoy", legendShift: "Turno", legendEmpty: "· = sin asignar", signoff: "Firma del supervisor:", floor: "Colocar en el taller. Actualizar según cambios." },
};

export default function ScheduleWallChart() {
  const [params, setParams] = useSearchParams();
  const anchor = useMemo(() => weekStart(params.get("week") ? parseIso(params.get("week")) : new Date()), [params]);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(anchor, i)), [anchor]);
  const from = iso(days[0]); const to = iso(days[6]);
  const langMode = params.get("lang") || "en"; // "en" | "es" | "both"
  const setLangMode = (v) => { const p = new URLSearchParams(params); p.set("lang", v); setParams(p, { replace: true }); };

  const [staff, setStaff] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [settings, setSettings] = useState({ company_name: "US Operations" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [s, sch, st] = await Promise.all([
        api.get("/staff"),
        api.get(`/schedules?date_from=${from}&date_to=${to}`),
        api.get("/settings"),
      ]);
      setStaff(s.data.filter((x) => x.active));
      setSchedules(sch.data);
      setSettings(st.data);
      setLoading(false);
    })();
  }, [from, to]);

  const byKey = useMemo(() => {
    const m = new Map();
    for (const s of schedules) m.set(`${s.staff_id}|${s.date}`, s);
    return m;
  }, [schedules]);

  // Only show staff who have at least one shift in the week — keeps the wall chart tight.
  const staffToShow = useMemo(() => {
    const active = new Set(schedules.map((s) => s.staff_id));
    return staff.filter((s) => active.has(s.id));
  }, [staff, schedules]);

  const totalShifts = schedules.length;
  const today = iso(new Date());

  if (loading) return <div className="p-8 font-display uppercase">Loading...</div>;

  return (
    <div data-testid="wall-chart-page">
      <style>{`
        @page { size: 17in 11in; margin: 0.25in; }
        @media print {
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .wc-page { page-break-after: avoid; }
        }
        .wc-page {
          width: 17in;
          min-height: 10.5in;
          padding: 0.15in 0.25in;
          background: #fff;
          color: #0f172a;
          font-family: 'IBM Plex Mono', monospace;
          box-sizing: border-box;
          margin: 12px auto;
          border: 2px solid #0f172a;
        }
        .wc-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          font-size: 11px;
        }
        .wc-table th, .wc-table td {
          border: 1.5px solid #0f172a;
          padding: 4px 5px;
          vertical-align: top;
        }
        .wc-table th {
          background: #f1f5f9;
          font-family: 'Archivo Black', sans-serif;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .wc-cell-op {
          font-family: 'Archivo Black', sans-serif;
          font-size: 14px;
          line-height: 1.1;
        }
        .wc-cell-name {
          font-size: 10px;
          color: #334155;
          margin-top: 2px;
          line-height: 1.2;
        }
        .wc-cell-shift {
          font-size: 10px;
          background: #dbeafe;
          border: 1px solid #1d4ed8;
          padding: 1px 4px;
          margin-top: 3px;
          display: inline-block;
          font-family: 'IBM Plex Mono', monospace;
          font-weight: 700;
        }
        .wc-cell-empty {
          color: #94a3b8;
          font-size: 20px;
          text-align: center;
        }
        .wc-today {
          background: #fef3c7 !important;
        }
      `}</style>

      <div className="flex items-center justify-between p-4 no-print flex-wrap gap-2">
        <Link to="/schedule" className="brut-tag brut-tag-white inline-flex items-center gap-1">
          <ArrowLeft size={10} weight="bold" /> Back to Schedule
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-mono-ui text-xs">
            {staffToShow.length} staff · {totalShifts} shift{totalShifts === 1 ? "" : "s"} · {from} → {to}
          </div>
          <div className="inline-flex border-2 border-slate-900" data-testid="wc-lang-toggle">
            {["en", "es", "both"].map((l) => (
              <button key={l} onClick={() => setLangMode(l)} className={`px-2 py-1 text-xs font-display uppercase ${langMode === l ? "bg-slate-900 text-white" : "bg-white"}`} data-testid={`wc-lang-${l}`}>
                {l === "both" ? "EN+ES" : l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <button onClick={() => window.print()} className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2" data-testid="print-wall-chart-btn">
          <Printer size={14} weight="bold" /> Print 11×17 Wall Chart
        </button>
      </div>

      <div className="wc-page" data-testid="wall-chart-sheet">
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, borderBottom: "3px solid #0f172a", paddingBottom: 8 }}>
          <img src="/logo-gate.jpg" alt="GATE" style={{ width: 56, height: 56, border: "2px solid #0f172a", objectFit: "cover" }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 11, letterSpacing: 3, color: "#1d4ed8" }}>
              // {settings.company_name?.toUpperCase()} — {I18N[langMode === "es" ? "es" : "en"].kicker}
              {langMode === "both" && <span style={{ marginLeft: 6, color: "#64748b" }}>/ {I18N.es.kicker}</span>}
            </div>
            <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 34, lineHeight: 1, marginTop: 4 }}>
              {I18N[langMode === "es" ? "es" : "en"].title}
            </div>
            {langMode === "both" && (
              <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 18, lineHeight: 1, marginTop: 2, color: "#334155" }}>
                {I18N.es.title}
              </div>
            )}
            <div style={{ fontSize: 12, marginTop: 3, color: "#334155" }}>
              {I18N[langMode === "es" ? "es" : "en"].weekOf} <b>{prettyDate(days[0])}</b> — <b>{prettyDate(days[6])}, {days[6].getFullYear()}</b>
            </div>
          </div>
          <div style={{ textAlign: "right", fontSize: 10, color: "#64748b" }}>
            <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 14, color: "#0f172a" }}>
              {staffToShow.length} {I18N[langMode === "es" ? "es" : "en"].stat}
            </div>
            <div style={{ marginTop: 4 }}>{I18N[langMode === "es" ? "es" : "en"].printed} {new Date().toLocaleString()}</div>
          </div>
        </div>

        {/* Grid */}
        {staffToShow.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#64748b", fontFamily: "Archivo Black, sans-serif", fontSize: 18 }}>
            No shifts scheduled for this week.
          </div>
        ) : (
          <table className="wc-table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th style={{ width: "12%" }}>Staff</th>
                {days.map((d, i) => {
              // ⤵ (renamed to avoid `_` prefix)
              const dIso = iso(d);
              return (
                <th key={i} className={dIso === today ? "wc-today" : ""}>
                  <div>{DOW[i]}</div>
                  <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 10, fontWeight: 400, marginTop: 2 }}>
                    {prettyDate(d)}
                  </div>
                </th>
              );
                })}
              </tr>
            </thead>
            <tbody>
              {staffToShow.map((s) => (
                <tr key={s.id}>
                  <td style={{ background: "#f8fafc" }}>
                    <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 12, lineHeight: 1.1 }}>{s.name}</div>
                    <div style={{ fontSize: 9, color: "#64748b", marginTop: 2 }}>{s.designation}</div>
                    {s.code && <div style={{ fontSize: 9, color: "#94a3b8", fontFamily: "IBM Plex Mono, monospace" }}>{s.code}</div>}
                  </td>
                  {days.map((d, i) => {
                    const dIso = iso(d);
                    const sch = byKey.get(`${s.id}|${dIso}`);
                    if (!sch) {
                      return <td key={i} className={`wc-cell-empty ${dIso === today ? "wc-today" : ""}`}>·</td>;
                    }
                    return (
                      <td key={i} className={dIso === today ? "wc-today" : ""} style={{ borderLeft: `4px solid ${(sch.operations && sch.operations[0]?.color) || "#cbd5e1"}` }}>
                        {(sch.operations && sch.operations.length > 0 ? sch.operations : [{ code: sch.operation_code, name: sch.operation_name }]).map((op, oi) => (
                          <div key={oi} style={{ marginTop: oi > 0 ? 4 : 0 }}>
                            <div className="wc-cell-op" style={{ color: op.color || "#0f172a" }}>{op.code || "—"}</div>
                            <div className="wc-cell-name">{op.name}</div>
                          </div>
                        ))}
                        <div className="wc-cell-shift">{sch.shift_start}–{sch.shift_end}</div>
                        {sch.notes && (
                          <div style={{ fontSize: 9, color: "#334155", marginTop: 3, fontStyle: "italic" }}>{sch.notes}</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Footer */}
        <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "flex-end", borderTop: "2px solid #0f172a", paddingTop: 8, fontSize: 10 }}>
          <div style={{ display: "flex", gap: 16 }}>
            <div><span className="wc-cell-shift">HH:MM–HH:MM</span> Shift window</div>
            <div><span style={{ background: "#fef3c7", border: "1px solid #b45309", padding: "1px 8px" }}>Today</span></div>
            <div>· = no shift assigned</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div><b>Supervisor sign-off:</b> ______________________</div>
            <div style={{ marginTop: 3, color: "#64748b" }}>Post on shop floor. Update as changes occur.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
